const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

async function request(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const detail = data && data.detail;
    let msg = `Request failed (${res.status})`;
    let reason = null;
    if (typeof detail === "string") msg = detail;
    else if (detail && typeof detail === "object") {
      reason = detail.reason || null;
      msg = detail.reason || JSON.stringify(detail);
    } else if (data && data.message) msg = data.message;
    const err = new Error(msg);
    err.status = res.status;
    err.reason = reason;
    throw err;
  }
  return data;
}

export const api = {
  me: () => request("/auth/me"),
  signup: (body) => request("/auth/signup", { method: "POST", body: JSON.stringify(body) }),
  login: (body) => request("/auth/login", { method: "POST", body: JSON.stringify(body) }),
  logout: () => request("/auth/logout", { method: "POST" }),
  emergentSession: (sessionId) =>
    request("/auth/session", { method: "POST", headers: { "X-Session-ID": sessionId } }),

  // Phase 2
  listCourses: (params = {}) => {
    const q = new URLSearchParams();
    if (params.band) q.set("band", params.band);
    if (params.big_idea) q.set("big_idea", params.big_idea);
    if (params.q) q.set("q", params.q);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request(`/courses${suffix}`);
  },
  getCourse: (id) => request(`/courses/${id}`),
  enroll: (course_id) => request(`/enrollments`, { method: "POST", body: JSON.stringify({ course_id }) }),
  myEnrollments: () => request(`/enrollments`),
  getModule: (id) => request(`/modules/${id}`),
  completeModule: (id) => request(`/modules/${id}/complete`, { method: "POST" }),

  // Phase 8 — Stripe unlock
  unlockQuote: (body) => request(`/unlock/quote`, { method: "POST", body: JSON.stringify(body) }),
  createOrder: (body) => request(`/unlock/orders`, { method: "POST", body: JSON.stringify(body) }),
  createCheckout: (order_id, origin_url) =>
    request(`/unlock/orders/${order_id}/checkout`, {
      method: "POST",
      body: JSON.stringify({ origin_url }),
    }),
  confirmOrder: (order_id) => request(`/unlock/orders/${order_id}/confirm`, { method: "POST" }),
  cancelOrder: (order_id) => request(`/unlock/orders/${order_id}/cancel`, { method: "POST" }),
  listOrders: () => request(`/unlock/orders`),

  // Phase 5 — exams + certificates
  examMeta: (course_id) => request(`/exams/${course_id}`),
  startAttempt: (course_id) => request(`/exams/${course_id}/attempts`, { method: "POST" }),
  answerQuestion: (attempt_id, question_id, selected_key) =>
    request(`/exams/attempts/${attempt_id}/answer`, {
      method: "POST",
      body: JSON.stringify({ question_id, selected_key }),
    }),
  submitAttempt: (attempt_id) => request(`/exams/attempts/${attempt_id}/submit`, { method: "POST" }),
  getAttempt: (attempt_id) => request(`/exams/attempts/${attempt_id}`),
  listAttempts: (course_id) => request(`/exams/${course_id}/attempts`),
  myCertificates: () => request(`/certificates/me`),
  getCertificate: (cid) => request(`/certificates/${cid}`),
  verifyCertificate: (cid) => request(`/certificates/${cid}/verify`),

  // Phase 6 — Admin
  adminListCourses: () => request(`/admin/courses`),
  adminCreateCourse: (body) => request(`/admin/courses`, { method: "POST", body: JSON.stringify(body) }),
  adminPatchCourse: (cid, body) => request(`/admin/courses/${cid}`, { method: "PATCH", body: JSON.stringify(body) }),
  adminDeleteCourse: (cid) => request(`/admin/courses/${cid}`, { method: "DELETE" }),
  adminListModules: (cid) => request(`/admin/modules?course_id=${encodeURIComponent(cid)}`),
  adminCreateModule: (body) => request(`/admin/modules`, { method: "POST", body: JSON.stringify(body) }),
  adminPatchModule: (mid, body) => request(`/admin/modules/${mid}`, { method: "PATCH", body: JSON.stringify(body) }),
  adminDeleteModule: (mid) => request(`/admin/modules/${mid}`, { method: "DELETE" }),
  adminListQuestions: (cid) => request(`/admin/exam-questions?course_id=${encodeURIComponent(cid)}`),
  adminCreateQuestion: (body) => request(`/admin/exam-questions`, { method: "POST", body: JSON.stringify(body) }),
  adminPatchQuestion: (qid, body) => request(`/admin/exam-questions/${qid}`, { method: "PATCH", body: JSON.stringify(body) }),
  adminDeleteQuestion: (qid) => request(`/admin/exam-questions/${qid}`, { method: "DELETE" }),

  // Phase 6 — Super Admin
  saOverview: () => request(`/superadmin/analytics/overview`),
  saTimeseries: (metric, range = "30d") => request(`/superadmin/analytics/timeseries?metric=${metric}&range=${range}`),
  saGeo: (range = "30d") => request(`/superadmin/analytics/geo?range=${range}`),
  saLoginEvents: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    return request(`/superadmin/login-events?${q.toString()}`);
  },
  saListUsers: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    return request(`/superadmin/users?${q.toString()}`);
  },
  saPatchUser: (uid, body) => request(`/superadmin/users/${uid}`, { method: "PATCH", body: JSON.stringify(body) }),
  saDeleteUser: (uid) => request(`/superadmin/users/${uid}`, { method: "DELETE" }),
  saImpersonate: (uid) => request(`/superadmin/users/${uid}/impersonate`, { method: "POST" }),
  saEndImpersonation: () => request(`/superadmin/impersonate/end`, { method: "POST" }),
  saEnrollments: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
    return request(`/superadmin/enrollments?${q.toString()}`);
  },
  saForceUnlock: (enrollment_id, module_ids) =>
    request(`/superadmin/enrollments/${enrollment_id}/force-unlock`, {
      method: "POST", body: JSON.stringify({ module_ids }),
    }),
  saRevokeCert: (cid) => request(`/superadmin/certificates/${cid}/revoke`, { method: "POST" }),
  saReissueCert: (cid) => request(`/superadmin/certificates/${cid}/reissue`, { method: "POST" }),
  saListOrders: (status) =>
    request(`/superadmin/orders${status ? `?status=${status}` : ""}`),
  saListCerts: () => request(`/superadmin/certificates`),
  saSparky: () => request(`/superadmin/sparky/conversations`),
  saAttempts: (course_id) => request(`/superadmin/exam-attempts${course_id ? `?course_id=${course_id}` : ""}`),
  saExportUrl: (kind) => `${API}/superadmin/exports/${kind}.csv`,

  // Phase 7 — Seed
  saSeedStart: (body) => request(`/superadmin/seed/start`, { method: "POST", body: JSON.stringify(body || {}) }),
  saSeedJobs: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) q.set(k, v); });
    return request(`/superadmin/seed/jobs?${q.toString()}`);
  },
  saSeedJob: (jid) => request(`/superadmin/seed/jobs/${jid}`),
  saSeedCancel: (jid) => request(`/superadmin/seed/jobs/${jid}/cancel`, { method: "POST" }),
  saSeedForceReset: (jid) => request(`/superadmin/seed/jobs/${jid}/force-reset`, { method: "POST" }),
  saSeedZombies: (staleMinutes = 15) => request(`/superadmin/seed/jobs/zombies?stale_minutes=${staleMinutes}`),
};
